import { useState, useEffect } from 'react';
import { useApi, discoveryApiRef } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { parseEntityRef } from '@backstage/catalog-model';

export interface PlatformBooking {
  id: number;
  packageSlug: string;
  tenantAcronym: string;
  [key: string]: any;
}

/**
 * Hook to fetch all TDMS services for a tenant based on repository's system
 * Returns aggregated list of all services from all platform bookings
 */
export const useTdmsServicesData = (
  repositoryValue: string,
): {
  services: PlatformBooking[] | null;
  loading: boolean;
  error?: Error;
} => {
  const catalogApi = useApi(catalogApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const [services, setServices] = useState<PlatformBooking[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | undefined>();

  useEffect(() => {
    let isCancelled = false;

    const fetchTdmsServices = async () => {
      if (!repositoryValue) {
        setServices(null);
        setLoading(false);
        setError(undefined);
        return;
      }

      setLoading(true);
      setError(undefined);

      try {
        // Parse the entity reference to get the repository entity
        const entityRef = parseEntityRef(repositoryValue);

        // Get the repository entity from the catalog
        const entity = await catalogApi.getEntityByRef(entityRef);

        if (!entity || isCancelled) {
          setServices(null);
          setLoading(false);
          return;
        }

        // Extract system from spec.system
        const spec = entity.spec as any;
        const tenantAcronym = spec?.system;

        if (!tenantAcronym || isCancelled) {
          setServices(null);
          setLoading(false);
          return;
        }

        // Get TDMS backend base URL
        const tdmsBaseUrl = await discoveryApi.getBaseUrl('tdmsv1');

        // Fetch platform bookings for the tenant
        const bookingsResponse = await fetch(
          `${tdmsBaseUrl}/platformBookings/${tenantAcronym}/details?current=true`,
        );

        if (!bookingsResponse.ok) {
          throw new Error(
            `Failed to fetch TDMS bookings: ${bookingsResponse.statusText}`,
          );
        }

        const bookings: PlatformBooking[] = await bookingsResponse.json();

        if (isCancelled) {
          return;
        }

        if (!bookings || bookings.length === 0) {
          // No bookings found
          setServices([]);
          setLoading(false);
          return;
        }

        setServices(bookings);
      } catch (err) {
        if (!isCancelled) {
          setError(err as Error);
          setServices(null);
        }
      } finally {
        if (!isCancelled) {
          setLoading(false);
        }
      }
    };

    fetchTdmsServices();

    // Cleanup function to cancel pending requests
    return () => {
      isCancelled = true;
    };
  }, [repositoryValue, catalogApi, discoveryApi]);

  return { services, loading, error };
};

/**
 * Helper function to check if a specific service exists in the services array
 * @param services - Array of TDMS services or null
 * @param slug - Service slug to search for (case-sensitive exact match)
 * @returns true if service exists, false otherwise
 */
export const hasService = (
  services: PlatformBooking[] | null,
  slug: string,
): boolean => {
  if (!services || services.length === 0) {
    return false;
  }

  return services.some(service => service.packageSlug === slug);
};
